import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import PermissionWrapper from '@/components/permission-wrapper/permission-wrapper-component';
import Styled from './view-edit-delete-style';

/**
 * ContextMenuViewEditDelete component for generic use in Table component.
 * @param {object} props The props object.
 * @param {Function} props.onView The view function.
 * @param {Function} props.onEdit The edit function.
 * @param {Function} props.onDelete The delete function.
 * @param {string} props.feature The feature name.
 * @param {Function} props.onDuplicate The duplicate function.
 * @param {Function} props.onPreview The preview function.
 * @param {string} props.previewText The preview text.
 * @param {string} props.viewText The view text.
 * @param {boolean} props.isPopupOpen The popup open state.
 * @param {Function} props.setIsPopupOpen The popup open state function.
 * @param {object} props.position The position of the popup.
 * @returns {JSX.Element}
 */
const ContextMenuViewEditDelete = ({
  onView,
  onEdit,
  onDelete,
  onDuplicate,
  onPreview,
  previewText,
  feature,
  viewText,
  isPopupOpen,
  setIsPopupOpen,
  position,
}) => {
  const popupContainerRef = useRef(null);
  const popupMenuRef = useRef(null);

  const handleItemClick = () => {
    setIsPopupOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popupContainerRef.current &&
        !popupContainerRef.current.contains(event.target)
      ) {
        setIsPopupOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setIsPopupOpen]);

  return (
    <Styled.PopupContainer ref={popupContainerRef}>
      {isPopupOpen && (
        <Styled.PopupMenu ref={popupMenuRef} $position={position}>
          {onView && (
            <PermissionWrapper permissions={[`${feature}.read`]}>
              <Styled.MenuItem
                onClick={() => {
                  onView();
                  handleItemClick();
                }}
              >
                <Styled.ViewIcon />
                <Styled.Text>{viewText ?? 'View'}</Styled.Text>
              </Styled.MenuItem>
            </PermissionWrapper>
          )}
          {onPreview && (
            <PermissionWrapper permissions={[`${feature}.read`]}>
              <Styled.MenuItem
                onClick={() => {
                  onPreview();
                  handleItemClick();
                }}
              >
                <Styled.ViewIcon />
                <Styled.Text>{previewText ?? 'Preview'}</Styled.Text>
              </Styled.MenuItem>
            </PermissionWrapper>
          )}
          {onDuplicate && (
            <PermissionWrapper permissions={[`${feature}.create`]}>
              <Styled.MenuItem
                onClick={() => {
                  onDuplicate();
                  handleItemClick();
                }}
              >
                <Styled.DuplicateIcon />
                <Styled.Text>Duplicate</Styled.Text>
              </Styled.MenuItem>
            </PermissionWrapper>
          )}
          {onEdit && (
            <PermissionWrapper permissions={[`${feature}.update`]}>
              <Styled.MenuItem
                onClick={() => {
                  onEdit();
                  handleItemClick();
                }}
              >
                <Styled.EditIcon />
                <Styled.Text>Edit</Styled.Text>
              </Styled.MenuItem>
            </PermissionWrapper>
          )}
          {onDelete && (
            <PermissionWrapper permissions={[`${feature}.delete`]}>
              <Styled.MenuItem
                onClick={() => {
                  onDelete();
                  handleItemClick();
                }}
              >
                <Styled.DeleteIcon />
                <Styled.Red>Delete</Styled.Red>
              </Styled.MenuItem>
            </PermissionWrapper>
          )}
        </Styled.PopupMenu>
      )}
    </Styled.PopupContainer>
  );
};

ContextMenuViewEditDelete.propTypes = {
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  onDuplicate: PropTypes.func,
  onView: PropTypes.func,
  onPreview: PropTypes.func,
  previewText: PropTypes.string,
  feature: PropTypes.string.isRequired,
  viewText: PropTypes.string,
  isPopupOpen: PropTypes.bool,
  setIsPopupOpen: PropTypes.func,
  position: PropTypes.object,
};

ContextMenuViewEditDelete.defaultProps = {
  onDelete: undefined,
  onEdit: undefined,
  onView: undefined,
  onDuplicate: undefined,
  viewText: undefined,
  onPreview: undefined,
  previewText: undefined,
  isPopupOpen: false,
  setIsPopupOpen: () => {},
  position: { x: 0, y: 0 },
};

export default ContextMenuViewEditDelete;
